<template>
    <form-wrapper
        :sections="[
            { id: 'when', label: 'When' },
            { id: 'type', label: 'Type' },
            { id: 'reason', label: 'Reason' },
        ]"
        :hideNav="!!props.reservedDateId"
    >
        <vx-expandable-card id="when">
            <template v-slot:header>
                <div class="flex items-center pb-3">
                    <span class="pe-4 me-4 border-y-0 border-s-0 border-e border-solid border-e-slate-400"> When </span>
                    <span class="text-sm font-semibold text-slate-700 me-1">
                        {{ dateFormatted || '-' }}
                    </span>
                </div>
            </template>

            <template v-slot:content>
                <vx-date-time-picker
                    :modelValue="dateAsDate"
                    @update:modelValue="onDateChange"
                    :timezone="getClientTz()"
                    :minDate="dayjs().tz(getClientTz()).toDate()"
                    :maxDate="maxDate"
                    :disabledDates="reservedDisabledDates"
                    :attributes="reservedDateAttributes"
                    mode="date"
                    :error="displayFirstError(validation.date)"
                    aria-label="Date to reserve"
                >
                    <template v-slot:dayPopover="{ day }">
                        <div v-if="getReservedDateForDay(day.id)" class="text-sm max-w-[196px]">
                            <div class="font-bold text-slate-700 break-all truncate">Already Reserved</div>
                            <div v-if="getReservedDateForDay(day.id)?.reason" class="text-xs">
                                {{ getReservedDateForDay(day.id)?.reason }}
                            </div>
                            <div class="flex gap-1 mt-1">
                                <vx-badge v-if="getReservedDateForDay(day.id)?.marketing" color="slate" size="xs">
                                    Marketing
                                </vx-badge>
                                <vx-badge v-if="getReservedDateForDay(day.id)?.transactional" color="slate" size="xs">
                                    Transactional
                                </vx-badge>
                            </div>
                            <router-button
                                size="sm"
                                color="muted-light"
                                outline
                                class="mt-2 w-full"
                                :to="{ name: 'reserved-dates.edit', params: { id: getReservedDateForDay(day.id)?.id } }"
                            >
                                Edit
                                <font-awesome-icon widthAuto :icon="faFilePen" />
                            </router-button>
                        </div>
                    </template>
                </vx-date-time-picker>
            </template>
        </vx-expandable-card>

        <vx-expandable-card id="type">
            <template v-slot:header>Type</template>
            <template v-slot:headerSubtitle> Which type should be reserved? </template>

            <template v-slot:content>
                <audience-type-selector
                    :marketing="reservedDate.marketing"
                    :transactional="reservedDate.transactional"
                    :error="displayFirstError(validation.type)"
                    @update:marketing="reservedDate.marketing = $event"
                    @update:transactional="reservedDate.transactional = $event"
                />
            </template>
        </vx-expandable-card>

        <vx-expandable-card id="reason">
            <template v-slot:header>
                <div class="flex gap-2 items-center">
                    <span>Reason</span>
                    <vx-badge color="violet" size="xs">
                        <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                        <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                    </vx-badge>
                </div>
            </template>

            <template v-slot:content>
                <vx-input
                    v-model="reservedDate.reason"
                    size="lg"
                    :rows="4"
                    :maxlength="80"
                    :counter="true"
                    :error="displayFirstError(validation.reason)"
                />
            </template>
        </vx-expandable-card>

        <vx-sticky-footer>
            <router-button :to="{ name: 'reserved-dates' }" class="w-20" size="lg" color="muted">
                Cancel
            </router-button>
            <vx-button
                @click="submit()"
                data-test="reserved-date-save"
                class="grow"
                size="lg"
                color="primary"
                :loading="processing"
            >
                {{ props.reservedDateId ? 'Update' : 'Reserve Date' }}
            </vx-button>
        </vx-sticky-footer>
    </form-wrapper>
</template>

<script lang="ts" setup>
import { faFilePen, faPencilSlash, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import RouterButton from '~/components/general/RouterButton.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import {
    VxBadge,
    VxButton,
    VxDateTimePicker,
    VxExpandableCard,
    VxInput,
    VxStickyFooter,
} from '@voxie/frontend-components';
import type { VxDateTimePickerProps } from '@voxie/frontend-components';
import AudienceTypeSelector from '~/components/reserved-dates/AudienceTypeSelector.vue';
import { defaultReservedDate, useReservedDateDetails } from '~/components/reserved-dates/useReservedDateDetails';
import { displayFirstError } from '~/utils/validation';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { required, maxLength, helpers } from '@vuelidate/validators';
import { computed, onMounted, ref } from 'vue';
import dayjs from '~/utils/dayjs';
import { getClientTz } from '~/utils/date';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { fetchReservedDatesInRange } from '~/services/reservedDates/fetchReservedDatesInRange';
import type { ReservedDate } from '~/services/reservedDates/reservedDatesClient.types';

const props = defineProps<{
    reservedDateId?: number;
}>();

const { reservedDate, save, onMountedReservedDate } = useReservedDateDetails();

const existingReservedDates = ref<ReservedDate[]>([]);

onMounted(() => {
    const today = dayjs().tz(getClientTz()).format('YYYY-MM-DD');
    const endDate = dayjs().tz(getClientTz()).add(3, 'month').endOf('month').format('YYYY-MM-DD');
    fetchReservedDatesInRange(Spark.state.currentTeam.id, today, endDate)
        .then((dates) => {
            existingReservedDates.value = dates;
        })
        .catch((e) => console.error(e));
});

const filteredReservedDates = computed(() => {
    return existingReservedDates.value.filter((rd) => rd.id !== props.reservedDateId);
});

const reservedDisabledDates = computed(() => {
    return filteredReservedDates.value.map((rd) => dayjs.utc(rd.date).hour(12).toDate());
});

const reservedDateAttributes = computed((): NonNullable<VxDateTimePickerProps['attributes']> => {
    const dates = reservedDisabledDates.value;

    if (0 === dates.length) {
        return [];
    }

    return [
        {
            dates,
            popover: {
                placement: 'top',
            },
        },
    ];
});

const getReservedDateForDay = (dayId: string): ReservedDate | undefined => {
    const dateKey = dayjs(dayId).format('YYYY-MM-DD');
    return existingReservedDates.value.find((rd) => {
        return rd.id !== props.reservedDateId && rd.date === dateKey;
    });
};

const maxDate = computed((): Date => {
    return dayjs().tz(getClientTz()).add(3, 'month').endOf('month').toDate();
});

const dateAsDate = computed((): Date | undefined => {
    if ('' === reservedDate.value.date) {
        return undefined;
    }
    const parsed = dayjs.utc(reservedDate.value.date).hour(12);
    return parsed.isValid() ? parsed.toDate() : undefined;
});

const dateFormatted = computed(() => {
    if ('' === reservedDate.value.date) {
        return '';
    }
    const parsed = dayjs.utc(reservedDate.value.date);
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : '';
});

const onDateChange = (date?: Date) => {
    reservedDate.value.date = date ? dayjs(date).tz(getClientTz()).format('YYYY-MM-DD') : '';
};

const atLeastOneType = helpers.withMessage(
    'At least one audience type must be selected.',
    () => reservedDate.value.marketing || reservedDate.value.transactional
);

const { validation, submit, processing } = useFormSubmission({
    data: reservedDate,
    validation: {
        date: { required },
        type: { atLeastOneType },
        reason: { maxLength: maxLength(80) },
    },
    submit: async () => {
        return await save();
    },
    reset: () => {
        if (!props.reservedDateId) {
            reservedDate.value = defaultReservedDate();
        }
    },
    preserveScroll: () => Boolean(props.reservedDateId),
    notification: () => ({
        message: `Reserved date ${props.reservedDateId ? 'updated' : 'created'}`,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Reserved Dates',
            to: { name: 'reserved-dates' },
        },
    }),
});

onMountedReservedDate(() => {
    if (props.reservedDateId) {
        validation.value.$reset();
    }
});
</script>
